import { api } from 'store/api';
import { ApiResponse } from 'store/api/types';
import { TOKEN_ID_HEADER } from 'store/app/constants';
import { TifdReportRequest, UpdateTifdReportRequest } from './models';

/** POST /api/tifd/{applicationConnectionId}/reports/preview */
export const previewTifdReport = (
  applicationConnectionId: string,
  body: TifdReportRequest
): Promise<ApiResponse> =>
  api.post<ApiResponse>(`/tifd/${applicationConnectionId}/reports/preview`, {
    body,
    headers: {
      [TOKEN_ID_HEADER]: applicationConnectionId,
    },
  });

/** GET /api/tifd/{applicationConnectionId}/reports/{reportId} */
export const getTifdReportById = (
  applicationConnectionId: string,
  reportId: string
): Promise<ApiResponse> =>
  api.get<ApiResponse>(
    `/tifd/${applicationConnectionId}/reports/${reportId}`,
    {
      headers: {
        [TOKEN_ID_HEADER]: applicationConnectionId,
      },
    }
  );

/** POST /api/tifd/{applicationConnectionId}/reports */
export const saveTifdReport = (
  applicationConnectionId: string,
  body: TifdReportRequest
): Promise<ApiResponse> =>
  api.post<ApiResponse>(`/tifd/${applicationConnectionId}/reports`, {
    body,
    headers: {
      [TOKEN_ID_HEADER]: applicationConnectionId,
    },
  });

/** PUT /api/tifd/{applicationConnectionId}/reports/{reportId} */
export const updateTifdReport = (
  applicationConnectionId: string,
  reportId: string,
  body: UpdateTifdReportRequest
): Promise<ApiResponse> =>
  api.put<ApiResponse>(
    `/tifd/${applicationConnectionId}/reports/${reportId}`,
    {
      body,
      headers: {
        [TOKEN_ID_HEADER]: applicationConnectionId,
      },
    }
  );

export type TifdStatusRequest = {
  reportUrl?: string;
  emailUrl?: string;
  note?: string;
  emailSubject?: string;
  emailMessage?: string;
  includePreparer?: boolean;
  includeReviewer?: boolean;
};

/** POST /api/tifd/{applicationConnectionId}/reports/{reportId}/send-to-client */
export const sendTifdToClient = (
  applicationConnectionId: string,
  reportId: string,
  body: TifdStatusRequest
): Promise<ApiResponse> =>
  api.post<ApiResponse>(
    `/tifd/${applicationConnectionId}/reports/${reportId}/send-to-client`,
    {
      body,
      headers: {
        [TOKEN_ID_HEADER]: applicationConnectionId,
      },
    }
  );

/** POST /api/tifd/{applicationConnectionId}/reports/{reportId}/revert */
export const revertTifdReport = (
  applicationConnectionId: string,
  reportId: string,
  body: TifdStatusRequest
): Promise<ApiResponse> =>
  api.post<ApiResponse>(
    `/tifd/${applicationConnectionId}/reports/${reportId}/revert`,
    {
      body,
      headers: {
        [TOKEN_ID_HEADER]: applicationConnectionId,
      },
    }
  );

/** POST /api/tifd/{applicationConnectionId}/reports/{reportId}/approve */
export const approveTifdReport = (
  applicationConnectionId: string,
  reportId: string,
  reportUrl: string
): Promise<ApiResponse> =>
  api.post<ApiResponse>(
    `/tifd/${applicationConnectionId}/reports/${reportId}/approve`,
    {
      body: { reportUrl },
      headers: {
        [TOKEN_ID_HEADER]: applicationConnectionId,
      },
    }
  );

/** POST /api/tifd/{applicationConnectionId}/reports/{reportId}/send-for-approval */
export const sendTifdForApproval = (
  applicationConnectionId: string,
  reportId: string,
  reportUrl: string
): Promise<ApiResponse> =>
  api.post<ApiResponse>(
    `/tifd/${applicationConnectionId}/reports/${reportId}/send-for-approval`,
    {
      body: { reportUrl },
      headers: {
        [TOKEN_ID_HEADER]: applicationConnectionId,
      },
    }
  );

/** POST /api/tifd/{applicationConnectionId}/reports/{reportId}/submit */
export const submitTifdReport = (
  applicationConnectionId: string,
  reportId: string
): Promise<ApiResponse> =>
  api.post<ApiResponse>(
    `/tifd/${applicationConnectionId}/reports/${reportId}/submit`,
    {
      headers: {
        [TOKEN_ID_HEADER]: applicationConnectionId,
      },
    }
  );

/** POST /api/tifd/{applicationConnectionId}/reports/{reportId}/lock */
export const lockTifdReport = (
  applicationConnectionId: string,
  reportId: string
): Promise<ApiResponse> =>
  api.post<ApiResponse>(
    `/tifd/${applicationConnectionId}/reports/${reportId}/lock`,
    {
      headers: {
        [TOKEN_ID_HEADER]: applicationConnectionId,
      },
    }
  );

/** POST /api/tifd/{applicationConnectionId}/reports/{reportId}/cancel */
export const cancelTifdReport = (
  applicationConnectionId: string,
  reportId: string
): Promise<ApiResponse> =>
  api.post<ApiResponse>(
    `/tifd/${applicationConnectionId}/reports/${reportId}/cancel`,
    {
      headers: {
        [TOKEN_ID_HEADER]: applicationConnectionId,
      },
    }
  );

/** POST /api/tifd/{applicationConnectionId}/reports/{reportId}/excel */
export const fillTifdWorkbook = (
  applicationConnectionId: string,
  reportId: string,
  file: File
): Promise<Response> => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post<Response>(
    `/tifd/${applicationConnectionId}/reports/${reportId}/excel`,
    {
      body: formData,
      formData: true,
      returnRawResponse: true,
      headers: {
        [TOKEN_ID_HEADER]: applicationConnectionId,
        Accept: '*/*',
      },
    }
  );
};

/** GET /api/tifd/{applicationConnectionId}/mappings: every provider account (archived ones flagged) with its mapping. */
export const getTifdMappings = (
  applicationConnectionId: string
): Promise<ApiResponse> =>
  api.get<ApiResponse>(`/tifd/${applicationConnectionId}/mappings`, {
    headers: {
      [TOKEN_ID_HEADER]: applicationConnectionId,
    },
  });
